import { TicketLineDTO, TicketDTO, TicketDivisionDTO, BuyerCustomerDTO, InvoiceParamsFromPosDTO } from 'core/api';
import { Result, CustomError, handleServerErrors, PrintError } from 'core/infrastructure';
import { makeTicketReadyToClose, showCloseDocumentScreen } from 'pos/modules/helpers/document-creation';
import * as SplitTicketStore from './SplitTicketStore';
import { OrderingFacade, PrintFacade } from 'pos/facades';
import { Toaster } from 'core/ui-next';

export type TicketLineDivision = {
	line: TicketLineDTO;
	leftQty: Nullable<number>;
	rightQty: Nullable<number>;
};

type TicketDivision = { division: TicketDivisionDTO, lines: TicketLineDivision[] }

type ManualTicketDivisionStatus = TicketDivision & {
	mode: 'manual',
};

type AutoTicketDivisionStatus = TicketDivision & {
	mode: 'auto',
	totalParts: number;
	currentPart: number;
};

export type TicketDivisionStatus = AutoTicketDivisionStatus | ManualTicketDivisionStatus;

function createLineDivisions(division: TicketDivisionDTO): TicketLineDivision[] {

	const lines: TicketLineDivision[] = SplitTicketStore.getOriginalTicket().Lines.map(line => {

		const leftLine = division.Left.Lines.find(l => l.Id === line.Id);
		const rightLine = division.Right.Lines.find(l => l.Id === line.Id);

		return {
			line,
			leftQty: leftLine ? leftLine.Quantity : null,
			rightQty: rightLine ? rightLine.Quantity : null
		};
	});

	return lines;
}

export async function initAuto(ticket: TicketDTO, currentPart: number, totalParts: number): Promise<Result<TicketDivisionStatus, CustomError>> {

	const initResult = await SplitTicketStore.createDivision(ticket);
	if (!initResult.ok) {
		return Result.error(initResult.error);
	}

	const divideEnvenlyResult = await SplitTicketStore.divideEvenly(initResult.value, totalParts - currentPart);
	if (!divideEnvenlyResult.ok) {
		return Result.error(divideEnvenlyResult.error);
	}

	let status: TicketDivisionStatus = {
		mode: 'auto',
		division: divideEnvenlyResult.value,
		lines: createLineDivisions(divideEnvenlyResult.value),
		currentPart,
		totalParts
	};

	const pendingParts = totalParts - currentPart;
	if (status.division.Left.TableCompanions !== null && pendingParts > 0) {
		const tableCompanionsToMove = Math.trunc(status.division.Left.TableCompanions / pendingParts);
		for (let i = 0; i < tableCompanionsToMove; i++) {
			status = await moveOneTableCompanionsToRight(status);
		}
	}

	return Result.ok(status);
}

export async function initManual(ticket: TicketDTO): Promise<Result<TicketDivisionStatus, CustomError>> {

	const result = await SplitTicketStore.createDivision(ticket);
	if (!result.ok) {
		return Result.error(result.error);
	}

	return Result.ok({
		mode: 'manual',
		division: result.value,
		lines: createLineDivisions(result.value)
	});
}

export async function moveOneToLeft(status: TicketDivisionStatus, line: TicketLineDTO): Promise<TicketDivisionStatus> {

	if (status.mode === 'auto')
		return status;

	const lineDivision = status.lines.find(x => x.line.Id === line.Id)!;
	// Si no puedo mover más cantidad no tengo nada más que hacer
	if (!lineDivision.rightQty)
		return status;

	const result = await SplitTicketStore.moveOneUnitToLeft(status.division, { ...line, Quantity: lineDivision.rightQty });
	if (!result.ok) {
		return status;
	}

	return { ...status, division: result.value, lines: createLineDivisions(result.value) };
}

export async function moveOneToRight(status: TicketDivisionStatus, line: TicketLineDTO): Promise<TicketDivisionStatus> {

	if (status.mode === 'auto')
		return status;

	const lineDivision = status.lines.find(x => x.line.Id === line.Id)!;
	// Si no puedo mover más cantidad no tengo nada más que hacer
	if (!lineDivision.leftQty)
		return status;

	const result = await SplitTicketStore.moveOneUnitToRight(status.division, { ...line, Quantity: lineDivision.leftQty });
	if (!result.ok) {
		return status;
	}

	return { ...status, division: result.value, lines: createLineDivisions(result.value) };
}

export async function moveOnePartToRight(status: TicketDivisionStatus, line: TicketLineDTO, parts: number): Promise<TicketDivisionStatus> {

	if (status.mode === 'auto')
		return status;

	const lineDivision = status.lines.find(x => x.line.Id === line.Id)!;
	// Si no puedo mover más cantidad no tengo nada más que hacer
	if (!lineDivision.leftQty)
		return status;

	const result = await SplitTicketStore.moveOnePartToRight(status.division, line, parts);
	if (!result.ok) {
		return status;
	}

	return { ...status, division: result.value, lines: createLineDivisions(result.value), /* lineDivisionMode: 'unit' */ };
}

export async function moveOneTableCompanionsToLeft(status: TicketDivisionStatus): Promise<TicketDivisionStatus> {
	const result = await SplitTicketStore.moveOneTableCompanionToLeft(status.division);
	if (!result.ok) {
		return status;
	}

	return { ...status, division: result.value, lines: createLineDivisions(result.value) };
}

export async function moveOneTableCompanionsToRight(status: TicketDivisionStatus): Promise<TicketDivisionStatus> {
	const result = await SplitTicketStore.moveOneTableCompanionToRight(status.division);
	if (!result.ok) {
		return status;
	}

	return { ...status, division: result.value, lines: createLineDivisions(result.value) };
}

export async function print(status: TicketDivisionStatus): Promise<TicketDivisionStatus> {
	return beginClosing(status, async () => {
		const result = await SplitTicketStore.parkClosingTicket();
		if (!result.ok)
			return result;

		await handleServerErrors(() => PrintFacade.printTicket([SplitTicketStore.getClosingTicket().Id]), {
			onPrintError: async (error: PrintError) => { Toaster.error(error.message); },
		});
		return result;
	});
}


const closeClosingTicket = async () => {

	const result = await makeTicketReadyToClose({
		closeAs: 'Invoice',
		getTicket: SplitTicketStore.getClosingTicket,
		setFriendlyName: 'DISABLE_ASK_FOR_FRIENDLY_NAME',
		setTableCompanions: 'DISABLE_SET_TABLE_COMPANIONS'
	});

	if (!result.ok)
		return Result.error();

	return await showCloseDocumentScreen({
		layout: 'CloseTicket',
		setCustomer: async (customer: BuyerCustomerDTO) => {
			const result = await SplitTicketStore.safeSplit(async () => OrderingFacade.setBuyerCustomer(SplitTicketStore.getClosingTicket().Id, customer), 'right');

			if (result.ok)
				await SplitTicketStore.updateClosingTicket();

			return;
		},
		getDocument: () => {
			const ticket = SplitTicketStore.getClosingTicket();
			return {
				ticketIds: [ticket.Id],
				ticketGlobalId: ticket.GlobalId,
				customer: ticket.Customer,
				pendingAmount: ticket.PendingAmount,
				discounts: {
					cashDiscount: ticket.CashDiscount,
					discountPercentageInCash: ticket.DiscountPercentageInCash,
					discountPercentage: ticket.DiscountPercentage
				}
			};
		},
		closeDocument: async (params: InvoiceParamsFromPosDTO) => {
			return SplitTicketStore.invoiceClosingTicket(params);
		}
	});
};


export async function closeTicket(status: TicketDivisionStatus): Promise<TicketDivisionStatus> {
	return beginClosing(status, async () => await closeClosingTicket());
}

export async function move(status: TicketDivisionStatus): Promise<TicketDivisionStatus> {
	return beginClosing(status, async () => await SplitTicketStore.moveClosingTicket());
}

async function beginClosing(status: TicketDivisionStatus, closeFunc: () => Promise<Result>): Promise<TicketDivisionStatus> {

	if (status.division.Error.length)
		return status;

	if (status.division.Right.Lines.length === 0)
		return status;

	const beginClosingResult = await SplitTicketStore.beginClosingTicket(
		status.division.ClosingQuantities,
		status.division.Right.TableCompanions);

	if (!beginClosingResult.ok) {
		return status;
	}

	const result = await closeFunc();

	if (!result.ok) {
		await SplitTicketStore.cancelClosingTicket();
		return status;
	}

	const endClosingResult = await SplitTicketStore.endClosingTicket(
		status.division.ClosingQuantities,
		status.division.Left.TableCompanions
	);

	if (!endClosingResult.ok) {
		return status;
	}

	const newDivisionResult = status.mode === 'manual' ? await initManual(SplitTicketStore.getOriginalTicket()) : await initAuto(SplitTicketStore.getOriginalTicket(), status.currentPart + 1, status.totalParts);
	if (!newDivisionResult.ok) {
		return status;
	}

	return newDivisionResult.value;
}

